// ═══════════════════════════════════════════════════════════════════
//  SLOT TYPES (AB#779)
//
//  Every site publishes three event types, and a row on the grid —
//  free or booked — is one of three kinds because of which of them it
//  belongs to. The slug each kind books, its label, and who may book it
//  are all here, so nothing else in the calendar spells out a slug.
// ═══════════════════════════════════════════════════════════════════

import { canBookAppointments, canManageAppointments } from "@/lib/appointments-access"

/**
 * What kind of appointment a row is, or would be.
 *
 * - `public`: the `intake` event type — what the website and the phone line book, and staff too.
 * - `private`: the `private` event type — booked by staff for somebody they are already helping,
 *   on slots the public is never offered.
 * - `managed`: the `off-slot` event type — booked only by managers (`appointments:manage`).
 */
export type SlotType = "public" | "private" | "managed"

/** The Cal event type slugs a staff booking can name: one per slot type. */
export type BookableEventTypeSlug = "intake" | "private" | "off-slot"

export const SLOT_TYPES: readonly SlotType[] = ["public", "private", "managed"]

const EVENT_TYPE_SLUG: Record<SlotType, BookableEventTypeSlug> = {
  public: "intake",
  private: "private",
  managed: "off-slot",
}

/** How the grid, its key and the booking dialog name each kind. */
export const SLOT_TYPE_LABEL: Record<SlotType, string> = {
  public: "Public",
  private: "Private",
  managed: "Managed",
}

/** The event type a booking onto a row of this kind is made on. */
export function eventTypeSlugFor(slotType: SlotType): BookableEventTypeSlug {
  return EVENT_TYPE_SLUG[slotType]
}

/** The kind an event type slug is, or undefined for a slug that is none of the three. */
export function slotTypeForSlug(slug: string | null | undefined): SlotType | undefined {
  return SLOT_TYPES.find(type => EVENT_TYPE_SLUG[type] === slug)
}

/**
 * How an event type slug is named anywhere a booking is shown — "intake" → "Public", "private" →
 * "Private", "off-slot" → "Managed" — so every page uses the grid's names. A slug that is none of
 * the three is shown as it is; no slug is null.
 */
export function slotTypeLabelForSlug(slug: string | null | undefined): string | null {
  if (!slug) return null
  const slotType = slotTypeForSlug(slug)
  return slotType ? SLOT_TYPE_LABEL[slotType] : slug
}

/**
 * The Cal ids of a site's private and off-slot event types — what a booking's or a bookable window's
 * `eventTypeId` is matched against. Anything in neither is the public kind, which is also what an
 * id set nobody could read says: a calendar that cannot tell the types apart draws every row as the
 * ordinary bookable kind it was before there were three.
 */
export interface SlotTypeEventTypes {
  privateEventTypes: ReadonlySet<number>
  managedEventTypes: ReadonlySet<number>
}

export function slotTypeEventTypes(
  privateEventTypeIds: readonly number[] | undefined,
  offSlotEventTypeIds: readonly number[] | undefined,
): SlotTypeEventTypes {
  return {
    privateEventTypes: new Set(privateEventTypeIds ?? []),
    managedEventTypes: new Set(offSlotEventTypeIds ?? []),
  }
}

/** The kind of one event type, by Cal id. */
export function slotTypeForEventType(eventTypeId: number, types: SlotTypeEventTypes): SlotType {
  if (types.managedEventTypes.has(eventTypeId)) return "managed"
  if (types.privateEventTypes.has(eventTypeId)) return "private"
  return "public"
}

/**
 * The kind of a free row, from every event type whose window covers it.
 *
 * The most widely bookable kind wins: a row the public type reaches is a public row whatever else
 * reaches it, and a row both private and managed reach is one staff can still offer. A host is
 * bookable for one event type at a time, so in practice there is only ever one.
 */
export function slotTypeForWindows(eventTypeIds: readonly number[], types: SlotTypeEventTypes): SlotType {
  const kinds = new Set(eventTypeIds.map(id => slotTypeForEventType(id, types)))
  if (kinds.has("public") || kinds.size === 0) return "public"
  if (kinds.has("private")) return "private"
  return "managed"
}

/** A row's kind. Unmarked — availability never read — is the public kind. */
export function slotTypeOf(cell: { slotType?: SlotType }): SlotType {
  return cell.slotType ?? "public"
}

/**
 * The kinds this user may book from the staff calendar: all three with `appointments:manage`; Public
 * and Private with `appointments:book`; none otherwise. The API enforces the same split (403).
 */
export function bookableSlotTypes(permissions: string[]): SlotType[] {
  if (canManageAppointments(permissions)) return [...SLOT_TYPES]
  if (canBookAppointments(permissions)) return ["public", "private"]
  return []
}

/**
 * Whether this user may override a refusal: double-book a host already booked, or book a household
 * that already has an appointment ("Book anyway"). Both are `appointments:manage`'s alone.
 */
export function canOverrideBookingRefusals(permissions: string[]): boolean {
  return canManageAppointments(permissions)
}
